import React from 'react';
import { Box, Heading, Icon, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconRateUnhappyFilled } from '@zillow/constellation-icons';
import { useWizard, WizardSubStep } from '@zillow/zrs-rental-applications';
import { usePidRemainingAttempts, useTermSet } from '../../../contexts/IdVerificationContext';
import { RENTER_CANNOT_IDV_HELP_URL } from '../../../constants/urls';
import { IdvStep, TermSet } from '../../../../../models';

interface IncorrectAnswersProps {
  id: string;
}

function LandlordContent() {
  const pidRemainingAttempts = usePidRemainingAttempts();
  const remainingAttemptsText =
    pidRemainingAttempts === 2
      ? 'You have 2 attempts left.'
      : pidRemainingAttempts === 1
        ? 'You have 1 attempt left.'
        : '';
  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      <Icon size="lg" css={{ color: 'icon.neutral' }}>
        <IconRateUnhappyFilled />
      </Icon>
      <Heading level={2} textStyle="heading-sm">
        Your identity couldn’t be verified
      </Heading>
      <Paragraph>
        Some of your answers were incorrect, so we could not verify your identity. Please try again.{' '}
        {remainingAttemptsText}
      </Paragraph>
    </Box>
  );
}

function RenterContent() {
  const pidRemainingAttempts = usePidRemainingAttempts();
  const remainingAttemptsText =
    pidRemainingAttempts === 2
      ? 'We can try to verify your identity two more times.'
      : pidRemainingAttempts === 1
        ? 'We can try to verify your identity one more time.'
        : '';
  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default', alignItems: 'flex-start' }}>
      <Icon size="lg" css={{ color: 'icon.neutral' }}>
        <IconRateUnhappyFilled />
      </Icon>
      <Heading level={2} textStyle="heading-sm">
        Your identity couldn’t be verified
      </Heading>
      <Paragraph>We couldn’t verify your identity with the information provided.</Paragraph>
      <Paragraph>
        Please check that all information is correct and submit your identity verification again.{' '}
        {remainingAttemptsText}
      </Paragraph>
      <TextButton asChild>
        <a href={RENTER_CANNOT_IDV_HELP_URL} target="_blank" rel="noopener noreferrer">
          Learn more
        </a>
      </TextButton>
    </Box>
  );
}

export default function IncorrectAnswers({ id }: IncorrectAnswersProps) {
  const { currentSubStep, onRequestStep } = useWizard();
  const termSet = useTermSet();

  return (
    <WizardSubStep
      id={id}
      hidden={currentSubStep !== id}
      meta={{
        renderNextButton: 'Retry',
        renderBackButton: () => null,
      }}
      onRequestNext={(abortController: AbortController) => {
        abortController.abort();
        onRequestStep(IdvStep.KiqOnboardingStep);
      }}
      render={termSet === TermSet.LandlordScreening ? LandlordContent : RenterContent}
    />
  );
}
